import { Link } from '@tanstack/react-router';
import { ShoppingBag, User, Menu, X, Search } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore } from '@/stores/cartStore';
import { useAuth } from '@/contexts/AuthContext';

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <PromoBar />
      <Header />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function PromoBar() {
  return (
    <div className="bg-warm-800 text-warm-50 py-2 text-center text-sm">
      <p>Made in small batches | Rooted in Indian art traditions | Designed to be gifted & cherished</p>
    </div>
  );
}

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { openCart, getTotalItems } = useCartStore();
  const { isAuthenticated } = useAuth();
  const itemCount = getTotalItems();

  const navLinks = [
    { label: 'Shop All', href: '/products', isExternal: false },
    { label: 'Mugs', href: '/products?category=mugs', isExternal: true },
    { label: 'Coasters', href: '/products?category=coasters', isExternal: true },
    { label: 'Notebooks', href: '/products?category=notebooks', isExternal: true },
    { label: 'Wall Frames', href: '/products?category=frames', isExternal: true },
    { label: 'Calendars', href: '/products?category=calendars', isExternal: true },
    { label: 'About Aarti', href: '/about', isExternal: false },
  ];

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-warm-200">
      <div className="container-page">
        <div className="flex items-center justify-between h-36 sm:h-44">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/" className="flex items-center">
            <img
              src="/Kamalkari-Logo.jpg"
              alt="Kamalkari by Aarti"
              className="h-32 sm:h-40 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.isExternal ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-warm-700 hover:text-warm-900 transition-colors link-underline"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-warm-700 hover:text-warm-900 transition-colors link-underline"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button className="p-2 text-warm-700 hover:text-warm-900 transition-colors hidden sm:block">
              <Search className="w-5 h-5" />
            </button>

            <Link
              to={isAuthenticated ? '/account' : '/auth/sign-in'}
              className="p-2 text-warm-700 hover:text-warm-900 transition-colors"
            >
              <User className="w-5 h-5" />
            </Link>

            <button
              onClick={openCart}
              className="p-2 text-warm-700 hover:text-warm-900 transition-colors relative"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-madder text-white text-xs rounded-full flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-80 bg-background z-50 lg:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-warm-200">
                <span className="font-serif text-xl font-semibold">Menu</span>
                <button onClick={() => setMobileMenuOpen(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="p-4 space-y-4">
                {navLinks.map((link) =>
                  link.isExternal ? (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-lg text-warm-800 hover:text-warm-900"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-lg text-warm-800 hover:text-warm-900"
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-warm-900 text-warm-100 mt-20">
      <div className="container-page py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="mb-4">
              <img
                src="/Kamalkari-Logo.jpg"
                alt="Kamalkari by Aarti"
                className="h-40 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-warm-300 max-w-md mb-4">
              A handcrafted world of kolams, colours & conscious gifting.
              From sacred wall frames to everyday pouches, mugs, calendars & custom art —
              slow-crafted beauty with meaning.
            </p>
            <p className="text-warm-200 font-serif italic mb-6">
              Let's gift artfully — together.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-warm-400 hover:text-white transition-colors">
                Instagram
              </a>
              <a href="#" className="text-warm-400 hover:text-white transition-colors">
                Pinterest
              </a>
              <a href="#" className="text-warm-400 hover:text-white transition-colors">
                Facebook
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-white mb-4">Shop</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="text-warm-300 hover:text-white transition-colors">
                  All Products
                </Link>
              </li>
              <li>
                <a href="/products?category=mugs" className="text-warm-300 hover:text-white transition-colors">
                  Ceramic Mugs
                </a>
              </li>
              <li>
                <a href="/products?category=coasters" className="text-warm-300 hover:text-white transition-colors">
                  Coasters
                </a>
              </li>
              <li>
                <a href="/products?category=notebooks" className="text-warm-300 hover:text-white transition-colors">
                  Notebooks
                </a>
              </li>
              <li>
                <a href="/products?category=frames" className="text-warm-300 hover:text-white transition-colors">
                  Wall Frames
                </a>
              </li>
              <li>
                <a href="/products?category=bottles" className="text-warm-300 hover:text-white transition-colors">
                  Water Bottles
                </a>
              </li>
              <li>
                <a href="/products?category=calendars" className="text-warm-300 hover:text-white transition-colors">
                  Desk Calendars
                </a>
              </li>
              <li>
                <a href="/products?category=gift-hampers" className="text-warm-300 hover:text-white transition-colors">
                  Gift Hampers
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-white mb-4">Connect</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-warm-300 hover:text-white transition-colors">
                  About Aarti
                </Link>
              </li>
              <li>
                <a href="#" className="text-warm-300 hover:text-white transition-colors">
                  Custom Orders
                </a>
              </li>
              <li>
                <a href="#" className="text-warm-300 hover:text-white transition-colors">
                  Shipping Info
                </a>
              </li>
              <li>
                <a href="#" className="text-warm-300 hover:text-white transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-warm-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-warm-400 text-sm">
            &copy; {new Date().getFullYear()} Kamalkari by Aarti. All rights reserved.
          </p>
          <div className="flex gap-4 text-sm text-warm-400">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
